import type { AgendaReadModel } from './types';

/**
 * Client REST de l'agenda (lecture seule).
 * GET /gas/v1/agenda?lang=&scope=current|all|years=YYYY[,YYYY]
 *
 * Important : sans `X-WP-Nonce` (wp_rest), WordPress ignore le cookie de
 * session et traite la requête comme anonyme → publicOnly, archives
 * logged_in/member vides. Toujours passer rest_nonce quand disponible.
 */
function buildUrl(restBaseUrl: string, locale: string, scope: string): string {
    const base = String(restBaseUrl || '/wp-json/gas/v1').replace(/\/+$/, '');
    const url = new URL(`${base}/agenda`, window.location.origin);
    if (locale) {
        url.searchParams.set('lang', locale);
    }
    url.searchParams.set('scope', scope);
    return url.toString();
}

async function fetchAgenda(
    restBaseUrl: string,
    locale: string,
    scope: string,
    restNonce?: string,
): Promise<AgendaReadModel> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (restNonce) {
        headers['X-WP-Nonce'] = restNonce;
    }
    const res = await fetch(buildUrl(restBaseUrl, locale, scope), {
        method: 'GET',
        credentials: 'same-origin',
        headers,
    });
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }
    const json = (await res.json()) as AgendaReadModel | { data: AgendaReadModel };
    // GasReadModelRestController renvoie le DTO directement (pas d'enveloppe data).
    return (json as { data?: AgendaReadModel }).data ?? (json as AgendaReadModel);
}

/**
 * Charge le read-model complet (toutes les années + year_counts + online).
 * scope=current ne gardait que N / N-1 dans `years` puis publicOnly recalculait
 * year_counts → onglet Archives vide. On charge `all` pour 2026/2025/2024 + archives.
 */
export function fetchCurrent(
    restBaseUrl: string,
    locale: string,
    restNonce?: string,
): Promise<AgendaReadModel> {
    return fetchAgenda(restBaseUrl, locale, 'all', restNonce);
}

/** Charge une année d'archive (read-model filtré, remplace l'AJAX gas_agenda_load_archive_year). */
export function fetchArchiveYear(
    restBaseUrl: string,
    locale: string,
    year: number | string,
    restNonce?: string,
): Promise<AgendaReadModel> {
    return fetchAgenda(restBaseUrl, locale, `years=${year}`, restNonce);
}
